import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Component for dynamic meta tags management
 * Updates document title and meta tags based on page
 */
const SEOHead = ({ 
  title, 
  description, 
  keywords,
  canonicalPath,
  ogImage,
  structuredData
}) => {
  const location = useLocation();
  const baseUrl = 'https://weqasa.it';
  
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }
    
    // Update meta description
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      }
    }
    
    // Update keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      }
    }
    
    // Update canonical URL
    const canonical = canonicalPath || location.pathname;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${baseUrl}${canonical}`);
    }
    
    // Update Open Graph tags
    if (title) {
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', title);
      
      let twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle) twitterTitle.setAttribute('content', title);
    }
    
    if (description) {
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) ogDescription.setAttribute('content', description);
      
      let twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (twitterDescription) twitterDescription.setAttribute('content', description);
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', `${baseUrl}${location.pathname}`);
    
    if (ogImage) {
      let ogImageTag = document.querySelector('meta[property="og:image"]');
      if (ogImageTag) ogImageTag.setAttribute('content', ogImage);
      
      let twitterImage = document.querySelector('meta[name="twitter:image"]');
      if (twitterImage) twitterImage.setAttribute('content', ogImage);
    }
    
    // Add structured data if provided
    if (structuredData) {
      // Remove existing dynamic structured data
      const existingScript = document.getElementById('dynamic-structured-data');
      if (existingScript) {
        existingScript.remove();
      }
      
      // Add new structured data
      const script = document.createElement('script');
      script.id = 'dynamic-structured-data';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
    
  }, [title, description, keywords, canonicalPath, ogImage, structuredData, location.pathname]);
  
  return null;
};

// SEO configurations for each page
export const SEO_CONFIG = {
  home: {
    title: 'Vendere casa velocemente | WEQASA',
    description: 'Vendi casa velocemente e al miglior prezzo. Scopri il metodo innovativo WEQASA per vendere senza perdere tempo. Piattaforma immobiliare italiana.',
    keywords: 'vendere casa velocemente, vendere casa al miglior prezzo, piattaforma immobiliare innovativa, vendere casa online'
  },
  properties: {
    title: 'Immobili in vendita | Acquistare casa al miglior prezzo | WEQASA',
    description: 'Trova casa al miglior prezzo su WEQASA. Scopri immobili selezionati in tutta Italia con trasparenza e risparmio garantito.',
    keywords: 'acquistare casa al miglior prezzo, comprare casa risparmiando, immobili in vendita Italia'
  },
  sellerRegister: {
    title: 'Vendere casa al miglior prezzo | WEQASA',
    description: 'Vendi il tuo immobile al miglior prezzo senza perdere tempo. Sistema innovativo con agenti verificati e massima trasparenza.',
    keywords: 'vendere casa al miglior prezzo, vendere casa senza perdere tempo, vendere immobile velocemente'
  },
  buyerRegister: {
    title: 'Acquistare casa al miglior prezzo | WEQASA',
    description: 'Acquista casa al miglior prezzo con WEQASA. Trasparenza totale, immobili verificati e risparmio garantito.',
    keywords: 'acquistare casa al miglior prezzo, comprare casa risparmiando, acquisto immobile Italia'
  },
  howItWorks: {
    title: 'Come vendere casa velocemente | WEQASA',
    description: 'Scopri come funziona WEQASA: il metodo innovativo per vendere e comprare casa al miglior prezzo in Italia.',
    keywords: 'come vendere casa velocemente, come comprare casa risparmiando, vendita immobiliare innovativa'
  },
  contact: {
    title: 'Contatti | WEQASA',
    description: 'Contatta WEQASA per vendere o acquistare casa al miglior prezzo. Assistenza dedicata per ogni esigenza immobiliare.',
    keywords: 'contatti weqasa, assistenza immobiliare, vendita casa Italia'
  },
  faq: {
    title: 'FAQ - Domande frequenti | WEQASA',
    description: 'Risposte alle domande più frequenti su come vendere e acquistare casa con WEQASA.',
    keywords: 'faq vendita casa, domande acquisto immobile, weqasa aiuto'
  }
};

// Helper function to generate property page SEO
export const getPropertySEO = (property) => {
  const type = property?.property_type || 'Immobile';
  const city = property?.city || '';
  const province = property?.province || '';
  const price = property?.starting_price ? `€${property.starting_price.toLocaleString('it-IT')}` : '';
  
  const typeMap = {
    'apartment': 'Appartamento',
    'villa': 'Villa',
    'house': 'Casa',
    'commercial': 'Locale commerciale',
    'land': 'Terreno',
    'office': 'Ufficio'
  };
  
  const localizedType = typeMap[type] || type;
  
  return {
    title: `${localizedType} in vendita a ${city} ${province ? `(${province})` : ''} | WEQASA`,
    description: `${localizedType} in vendita a ${city}${price ? ` a partire da ${price}` : ''}. Acquista al miglior prezzo con WEQASA, la piattaforma immobiliare innovativa.`,
    keywords: `${localizedType.toLowerCase()} vendita ${city}, comprare casa ${city}, immobili ${city}`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      "name": `${localizedType} in vendita a ${city}`,
      "description": property?.description || `${localizedType} in vendita`,
      "url": `https://weqasa.it/immobili/${property?.property_id}`,
      "datePosted": property?.created_at,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city,
        "addressRegion": province,
        "addressCountry": "IT"
      },
      "offers": {
        "@type": "Offer",
        "price": property?.starting_price,
        "priceCurrency": "EUR"
      }
    }
  };
};

export default SEOHead;
